type JsonValue = unknown

export const DATA_FILES = {
  siteConfig: 'site-config.json',
  highlights: 'highlights.json',
  services: 'services.json',
  projects: 'projects.json',
  news: 'news.json',
  homeHero: 'home-hero.json',
  partners: 'partners.json',
  aboutStats: 'about-stats.json',
  aboutIntro: 'about-intro.json',
  aboutTimeline: 'about-timeline.json',
  aboutValues: 'about-values.json',
  aboutGallery: 'about-gallery.json',
  myAdvantages: 'my-advantages.json',   // 典型案例页面  我们的优势数据
  basicInfo: 'home-about.json',  // 首页关于我们数据
  honor: "honor.json",
  positions: "positionsList.json",
  products: "products.json",   // 产品列表
  solutions: "solutions.json"  // 解决方案列表
} as const

export type DataKey = keyof typeof DATA_FILES

const cache = new Map<string, Promise<JsonValue>>()

const CANDIDATE_PREFIXES = ['/data/examples'] as const

async function fetchJson<T = JsonValue>(
  filename: string,
  init?: RequestInit & { timeoutMs?: number; signal?: AbortSignal }
): Promise<T> {
  const { timeoutMs = 15000, signal, ...rest } = init ?? {}

  const withTimeout = <R>(promise: Promise<R>) =>
    new Promise<R>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(`Fetch timeout: ${filename}`)), timeoutMs)
      promise.then(
        value => {
          clearTimeout(timer)
          resolve(value)
        },
        error => {
          clearTimeout(timer)
          reject(error)
        }
      )
    })

  let lastError: unknown
  for (const prefix of CANDIDATE_PREFIXES) {
    const url = `${prefix}/${filename}`
    try {
      const response = await withTimeout(fetch(url, { credentials: 'same-origin', signal, ...rest,
        cache: 'no-cache',  // 添加请求头，禁止浏览器缓存
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      }))
      if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`)
      return (await response.json()) as T
    } catch (error) {
      lastError = error
    }
  }

  throw new Error(`Failed to fetch ${filename}: ${String(lastError)}`)
}

export function loadData<K extends DataKey, T = JsonValue>(
  key: K,
  options?: RequestInit & { timeoutMs?: number; force?: boolean; signal?: AbortSignal }
): Promise<T> {
  const filename = DATA_FILES[key]
  const cacheKey = filename

  if (options?.force) cache.delete(cacheKey)

  if (!options?.force && cache.has(cacheKey)) {
    return cache.get(cacheKey)! as Promise<T>
  }

  const request = fetchJson<T>(filename, options).catch(error => {
    cache.delete(cacheKey)
    throw error
  })

  cache.set(cacheKey, request as Promise<JsonValue>)
  return request
}

export async function loadAll() {
  const entries = Object.keys(DATA_FILES).map(async key => {
    const dataKey = key as DataKey
    const value = await loadData(dataKey)
    return [dataKey, value] as const
  })

  const pairs = await Promise.all(entries)
  return Object.fromEntries(pairs) as Record<DataKey, JsonValue>
}

export function invalidateData(key?: DataKey) {
  if (!key) {
    cache.clear()
    return
  }
  const filename = DATA_FILES[key]
  cache.delete(filename)
}

export const dataClient = {
  loadData,
  loadAll,
  invalidateData
}
